import React, { useContext, useEffect, useMemo, useState } from "react"
import moment from "moment-timezone"
import firebase from "firebase"
import { BedDouble, Flag, Plane, Car, Upload as UploadIcon, Clock } from "lucide-react"

import { BookingContext } from "../../../utils/providers/isolated-booking"
import { db } from "../../../utils/firebase"
import { BOARD_TYPES } from "../../../utils/exports/board-types"
import PortalUpload from "../portal-upload"
import PortalImageViewer from "../portal-image-viewer"

import styles from "./timeline.module.scss"

// Buggy messaging — only shown when buggies are included in some form. When
// they aren't, we say nothing (no "not included" line).
const buggyMessages = {
	true: "Buggies are included in your package.",
	yes_greenfee: "Buggies are included in your package.",
	yes_prepaid: "Buggies are included and prepaid.",
	yes_reserved: "We've reserved buggies for you at the resort, to be paid locally (subject to availability).",
}

const buggyString = (buggies, comp) => {
	if (comp && buggyMessages[comp]) return buggyMessages[comp]
	if (buggies) return buggyMessages.true
	return null
}

// All portal dates are pinned to Europe/London (see the portal root); we are
// explicit here too so the itinerary never drifts for an overseas client.
const inUK = (seconds) => moment(seconds, "X").tz("Europe/London")

/**
 * Itinerary — the chronological trip timeline.
 *
 * Hotels (by check-in) and golf rounds (by tee time) are merged into a single
 * date-sorted list. Items without a date yet show as "Pending …" and sort to
 * the end. A flights/transfers card sits above the timeline.
 */
export default function Timeline() {
	const { bookingID, hotelsOrder, roundsOrder } = useContext(BookingContext)

	const [nodes, setNodes] = useState([])
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!bookingID) return
		let cancelled = false
		setLoading(true)

		const load = async () => {
			// Hotels — one node per stay, anchored to check-in.
			const hotelNodes = await Promise.all(
				(hotelsOrder || []).map(async (hid) => {
					const hotel = (await db.doc(`bookings/${bookingID}/hotels/${hid}`).get()).data() || {}
					let name = ""
					let image = ""
					if (hotel.resortID) {
						name = (await db.doc(`hotels/${hotel.resortID}`).get()).data()?.name || ""
						const imgs = await db.collection(`hotels/${hotel.resortID}/images`).limit(1).get()
						if (!imgs.empty) image = imgs.docs[0].data().public_url
					}
					const seconds = hotel.check_in?.seconds ?? null
					return {
						key: `hotel-${hid}`,
						type: "hotel",
						seconds,
						sort: seconds ?? Number.MAX_SAFE_INTEGER,
						name,
						image,
						nights: hotel.nights || 0,
						boardType: BOARD_TYPES[hotel.board_type] || "",
						rooms: Array.isArray(hotel.rooms) ? hotel.rooms.length : 0,
					}
				}),
			)

			// Rounds — one node per round, anchored to tee time.
			const roundNodes = await Promise.all(
				(roundsOrder || []).map(async (rid) => {
					const round = (await db.doc(`bookings/${bookingID}/rounds/${rid}`).get()).data() || {}
					let name = ""
					let image = ""
					if (round.courseID) {
						name = (await db.doc(`courses/${round.courseID}`).get()).data()?.name || ""
						const imgs = await db.collection(`courses/${round.courseID}/images`).limit(1).get()
						if (!imgs.empty) image = imgs.docs[0].data().public_url
					}
					const seconds = round.tee_time?.seconds ?? null
					return {
						key: `round-${rid}`,
						type: "round",
						seconds,
						sort: seconds ?? Number.MAX_SAFE_INTEGER,
						name,
						image,
						buggies: buggyString(round.buggies, round.comp_buggies),
					}
				}),
			)

			if (!cancelled) {
				setNodes([...hotelNodes, ...roundNodes])
				setLoading(false)
			}
		}

		load().catch((err) => {
			console.log("Timeline load failed:", err)
			if (!cancelled) setLoading(false)
		})

		return () => {
			cancelled = true
		}
	}, [bookingID, hotelsOrder, roundsOrder])

	// Stable sort by date; undated ("Pending") items fall to the end.
	const sorted = useMemo(() => [...nodes].sort((a, b) => a.sort - b.sort), [nodes])

	return (
		<div className={styles.itinerary}>
			<header className={styles.head}>
				<h1>Your itinerary</h1>
				<p>Everything in order, from arrival to your final round.</p>
			</header>

			<FlightsCard bookingID={bookingID} />

			{loading ? (
				<div className={styles.loading}>Loading your itinerary…</div>
			) : sorted.length === 0 ? (
				<div className={styles.empty}>Your itinerary is being put together. Check back soon, or contact your agent for details.</div>
			) : (
				<div className={styles.timeline}>
					{sorted.map((node, index) => (
						<TimelineItem
							key={node.key}
							node={node}
							isLast={index === sorted.length - 1}
						/>
					))}
				</div>
			)}
		</div>
	)
}

/**
 * A single timeline node (hotel stay or golf round).
 */
function TimelineItem({ node, isLast }) {
	const isHotel = node.type === "hotel"

	let when
	if (node.seconds) {
		when = isHotel ? `Check in ${inUK(node.seconds).format("ddd D MMM YYYY")}` : inUK(node.seconds).format("ddd D MMM YYYY [·] HH:mm")
	} else {
		when = isHotel ? "Pending check-in date" : "Pending tee time"
	}

	return (
		<div className={styles.item}>
			<div className={styles.rail}>
				<span className={`${styles.dot} ${isHotel ? styles.dotHotel : styles.dotRound}`}>{isHotel ? <BedDouble size={16} /> : <Flag size={16} />}</span>
				{!isLast && <span className={styles.line} />}
			</div>

			<div className={styles.card}>
				{node.image ? (
					<div
						className={styles.cardImage}
						style={{ backgroundImage: `url("${node.image}")` }}
					/>
				) : null}

				<div className={styles.cardBody}>
					<span className={styles.kicker}>{isHotel ? "Hotel stay" : "Golf round"}</span>
					<h3>{node.name || (isHotel ? "Hotel to be confirmed" : "Course to be confirmed")}</h3>

					<p className={`${styles.when} ${!node.seconds ? styles.pending : ""}`}>
						<Clock size={14} />
						{when}
					</p>

					{isHotel ? (
						<ul className={styles.facts}>
							{node.nights ? (
								<li>
									{node.nights} {node.nights === 1 ? "night" : "nights"}
									{node.seconds ? ` · check out ${inUK(node.seconds).add(node.nights, "days").format("ddd D MMM")}` : ""}
								</li>
							) : null}
							{node.boardType ? <li>{node.boardType}</li> : null}
							{node.rooms ? (
								<li>
									{node.rooms} {node.rooms === 1 ? "room" : "rooms"}
								</li>
							) : null}
						</ul>
					) : node.buggies ? (
						<p className={styles.buggies}>{node.buggies}</p>
					) : null}
				</div>
			</div>
		</div>
	)
}

/**
 * Flights & transfers card — shows the transfer partner (if set) and lets the
 * client upload / manage their flight-detail screenshots. Mirrors the legacy
 * transfers tab's behaviour (upload to bookings/{id}/images + a log entry).
 */
function FlightsCard({ bookingID }) {
	const { client, booking } = useContext(BookingContext)

	const [transferName, setTransferName] = useState("")
	const [images, setImages] = useState([])
	const [uploading, setUploading] = useState(false)
	const [viewerImage, setViewerImage] = useState(null)

	const transferID = booking?.transfer || ""
	const transfersCost = Number(booking?.margins?.transfers_cost || 0)
	const flightsCost = Number(booking?.margins?.flights_cost || 0)

	// What's worth showing: a transfer is "present" if a partner is listed OR
	// there's a transfers cost (a zero cost can mean it's bundled with the
	// hotel, so the listed partner is what counts). Flights are "present" when
	// there's a flights cost on the booking.
	const showTransfers = Boolean(transferID) || transfersCost > 0
	const showFlights = flightsCost > 0

	// Resolve the transfer partner name, if a transfer is set on the booking.
	useEffect(() => {
		if (!transferID) return
		let cancelled = false
		db.doc(`transfers/${transferID}`)
			.get()
			.then((doc) => {
				if (!cancelled) setTransferName(doc.data()?.name || "")
			})
			.catch(() => {})
		return () => {
			cancelled = true
		}
	}, [transferID])

	// Live list of uploaded flight-detail images (only needed for flights).
	useEffect(() => {
		if (!bookingID || !showFlights) return
		const unsubscribe = db.collection(`bookings/${bookingID}/images`).onSnapshot((snap) => {
			setImages(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
		})
		return () => unsubscribe()
	}, [bookingID, showFlights])

	const removeImage = async (id) => {
		await db.doc(`bookings/${bookingID}/images/${id}`).delete()
		await db.collection(`bookings/${bookingID}/logs`).add({
			created: firebase.firestore.FieldValue.serverTimestamp(),
			type: "FLIGHT_DETAILS_REMOVED",
			message: "Flight details were removed by the client",
			user: null,
			badge: "INFO",
		})
	}

	// Nothing relevant on this booking — hide the card entirely.
	if (!showTransfers && !showFlights) return null

	const title = showFlights && showTransfers ? "Flights & transfers" : showFlights ? "Flight details" : "Your transfers"

	return (
		<section className={styles.flights}>
			<div className={styles.flightsHead}>
				<span className={styles.flightsIcon}>{showFlights ? <Plane size={18} /> : <Car size={18} />}</span>
				<div className={styles.flightsText}>
					<h2>{title}</h2>
					{showTransfers ? (
						<p>{transferName ? `Transfer with ${transferName}` : "Your transfers are taken care of. Full details to follow."}</p>
					) : (
						<p>We just need your flight details to finish arranging your trip.</p>
					)}
				</div>
			</div>

			{showFlights && (
				<div className={styles.flightsBody}>
				<p className={styles.flightsHint}>
					{images.length > 0
						? "Thank you for uploading your flight details. If you need to add more, or remove your current ones, you can do that below."
						: "In order for us to organise your transfers correctly, we need to know what your flight details are. Please upload a screenshot of them here."}
				</p>

				{images.length > 0 ? (
					<div className={styles.thumbs}>
						{images.map((image) => (
							<button
								key={image.id}
								type="button"
								className={styles.thumb}
								onClick={() => setViewerImage(image)}
								aria-label="View flight detail screenshot">
								<img
									src={image.public_url}
									alt="Flight details"
								/>
							</button>
						))}
					</div>
				) : null}

				<button
					type="button"
					className={styles.uploadButton}
					onClick={() => setUploading(true)}>
					<UploadIcon size={16} />
					Upload flight details
				</button>
				</div>
			)}

			{uploading && (
				<PortalUpload
					bookingID={bookingID}
					clientID={client?.id}
					close={() => setUploading(false)}
				/>
			)}

			{viewerImage && (
				<PortalImageViewer
					image={viewerImage}
					onClose={() => setViewerImage(null)}
					onRemove={async (id) => {
						await removeImage(id)
						setViewerImage(null)
					}}
				/>
			)}
		</section>
	)
}
